import type {
	GraphModification,
	InstanceAttribute,
	Graph,
	KnowledgeObject,
} from "@lr-notitia/notitia-operator/schemas";
import { Ref, computed } from "vue";
import { api } from "@lr-notitia/notitia-operator/client";
import { handleGraphModification } from "./utils";
import { useDomainStore } from "../domain";
import { useInstanceWorkspacesStore } from "../i-workspace";
import { UserRole } from "@lr-notitia/notitia-operator/schemas";
import { useGraphStore } from "../graph";
import { useQuestionsStore } from "../questions";
import { useSymbolsStore } from "../symbols";

let domainStore: ReturnType<typeof useDomainStore>;
let instanceWorkspacesStore: ReturnType<typeof useInstanceWorkspacesStore>;
let graphStore: ReturnType<typeof useGraphStore>;
let questionsStore: ReturnType<typeof useQuestionsStore>;
let symbolsStore: ReturnType<typeof useSymbolsStore>;
function init() {
	domainStore = useDomainStore();
	instanceWorkspacesStore = useInstanceWorkspacesStore();
	graphStore = useGraphStore();
	questionsStore = useQuestionsStore();
	symbolsStore = useSymbolsStore();
}

export const instanceAttributes = (graph: Ref<Graph>) =>
	computed<InstanceAttribute[]>(
		() =>
			Object.values(graph.value.nodes).filter(
				(node: any) => node.type === "attribute" && node.layer === "instance",
			) as unknown as InstanceAttribute[],
	);
export function instanceAttribute(domainId: Ref<string>, graph: Ref<Graph>) {
	return {
		byId: (id: string) => byId(id, graph),
		update: (id: string, value: any) => update(domainId, graph, id, value),
		getHighlightMatches: (word: string) => getHighlightMatches(word),
	};
}

function byId(id: string, graph: Ref<Graph>): InstanceAttribute | undefined {
	let hit = graph.value.nodes[id] as KnowledgeObject;
	return hit && hit.type === "attribute" && hit.layer === "instance" ? (hit as InstanceAttribute) : undefined;
}

function update(domainId: Ref<string>, graph: Ref<Graph>, id: string, value: any): Promise<GraphModification> {
	if (!domainStore || !instanceWorkspacesStore || !questionsStore) init();
	return new Promise((resolve, reject) => {
		if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
		if (!graph.value.nodes[id]) return reject();
		if (!instanceWorkspacesStore.openedWorkspaceId) return reject();
		if (instanceWorkspacesStore.isOpenedWorkspaceLocked) return reject();

		// fragen umformulieren
		let geschlecht_concept_attr_id = "404e1ebe-a885-4763-b102-f0c2cb2bbd8f";
		let nachname_concept_attr_id = "94342ff8-984e-4dc2-81c5-0e47901bcdd3";
		let istMeldendePerson_concept_id = "1df8cfea-852a-4b0a-9ec2-00120261fd59";
		let instanceAttr = graph.value.nodes[id] as unknown as InstanceAttribute;
		let soll_umformulieren = false;
		if (
			instanceAttr.concept === geschlecht_concept_attr_id ||
			instanceAttr.concept === nachname_concept_attr_id ||
			instanceAttr.concept === istMeldendePerson_concept_id
		) {
			soll_umformulieren = true;
		}
		if (soll_umformulieren) {
			questionsStore.soll_umformulieren_counter += 1;
		}

		const attemptUpdate = (attempt: number = 1) => {
			if (!instanceWorkspacesStore.openedWorkspaceId) return reject();
			const delay = attempt === 1 ? 0 : 300;
			setTimeout(() => {
				api()
					.domain(domainId.value)
					.workspace(instanceWorkspacesStore.openedWorkspaceId!)
					.attribute(id)
					.put(value)
					.then((data) => {
						handleGraphModification(data.modification, data.metadata);
						resolve(data.modification);
					})
					.catch((error) => {
						const is401 = error?.response?.status === 401 || error?.status === 401;
						if (is401 && attempt < 3) {
							// Retry on 401
							attemptUpdate(attempt + 1);
						} else {
							reject();
						}
					})
					.finally(() => {
						// fragen umformulieren
						if (soll_umformulieren) {
							questionsStore.soll_umformulieren_counter -= 1;
						}
					});
			}, delay);
		};

		attemptUpdate();
	});
}

// TODO: add a score to the matches and sort by score
// TODO: maybe do hard string matching instead of includes
function getHighlightMatches(word: string) {
	if (!graphStore || !symbolsStore) init();
	let conceptAttributeMatches = graphStore.concept.attributes.filter((a) => {
		let datatype = graphStore.descriptor.datatype.byId(a.datatype);
		if (!datatype) return false;
		let datatypeTerm = symbolsStore.term({ id: datatype.refersTo, terminology: datatype.terminology });
		let enumerationItemsTerms = datatype.enumerationItems.map((enumItemId) => {
			let enumItem = graphStore.descriptor.enumItem.byId(enumItemId);
			return symbolsStore.term({
				id: enumItem?.refersTo,
				terminology: enumItem?.terminology,
			});
		});
		const attributeTermMatches = symbolsStore
			.term({ id: a.refersTo, terminology: a.terminology })
			.toLowerCase()
			.includes(word.toLowerCase());
		const datatypeTermMatches = datatypeTerm.toLowerCase().includes(word.toLowerCase());
		const enumerationItemsTermMatches = enumerationItemsTerms.some((e) =>
			e.toLowerCase().includes(word.toLowerCase()),
		);

		return attributeTermMatches || datatypeTermMatches || enumerationItemsTermMatches;
	});
	let instanceAttributeMatches = graphStore.instance.attributes.filter((a) =>
		conceptAttributeMatches.find((c) => c.id === a.concept),
	);
	return instanceAttributeMatches;
}
